import React, { useState, useMemo } from "react";
import { useFormatter } from "./../../../util/formatter";
import LoaderGraph from "../../general/LoaderGraph";
import fonts_config from "./fonts_config.json";
import pdfFonts from "pdfmake/build/vfs_fonts.js";
import pdfMake from "pdfmake/build/pdfmake.js";
import { Button, Row } from "react-bootstrap";
import { useSaving, useSolarCalc } from "../../../util/calculations";
import GYLogo from "../../../images/BASE64/GYLogo";
import accSignature from "../../../images/BASE64/accSignature";
import usePeakLabels from "../../../util/peakLabels";
import {
	getElectricBillSection,
	getNoFTCostElectricBillSection,
} from "./ElectricBillSection";
import moment from "moment-timezone";
import GYAxios from "../../../util/GYAxios";
import rfdc from 'rfdc';

const clone = rfdc();

pdfMake.vfs = pdfFonts.pdfMake ? pdfFonts.pdfMake.vfs : pdfMake.vfs;
pdfMake.fonts = fonts_config;

// convert centimeters to points (PDFMake use Point as a unit)
const cm_to_pt = cm => cm * 28.346;

// IMPORTANT: DO NOT CHANGE BELOW DOC DEFINITION
// AS IT IS FOR SHOWING THE OLD INVOICES
// invoice template before having envelope with window
const old_invoice_template = (docDefData) => {
	const { logo, entitySection, billingName, billingAddress, data, copyTypes, copyType, locationSection, period, peakLabels, discountON_display, solarON, priceON_disc, formatNumber, totalPriceON, discountOFF_display, solarOFF, priceOFF_disc, totalPriceOFF, isNoFTCostSite, discountFT_display, solarTotal, FTcost_disc, totalPriceFT, subtotalPrice, vatDisplay, VATPrice, specialDiscount_display, discountPrice, totalDue, holidays, param, paymentSection } = docDefData;
	const docDefinition = {
		pageSize: "A4",
		pageMargins: 0,
		content: [
			{ nodeName: 'DIV', margin: [40, 40], style: 'invoiceTHStyle', stack: [
			{
				nodeName: "DIV",
				columns: [
					{
						nodeName: "DIV",
						width: "50%",
						stack: [
							{
								image: logo,
								width: 150,
							},
							...entitySection,
							{
								text: "TO",
								style: ["boldHeader"],
							},
							{
								nodeName: "DIV",
								margin: [20, 10],
								stack: [
									{
										text: billingName,
										style: ["boldHeader"],
									},
									{
										text: billingAddress,
									},
									{
										text: `TAX ID : ${data.taxID}`,
									},
								],
							},
						],
					},
					{
						nodeName: "DIV",
						width: "50%",
						stack: [
							// second column consists of paragraphs
							{ text: "Invoice / Tax Invoice", style: "header_lg" },
							{ text: copyTypes[copyType], style: "sub_header_2", margin: 0 },
							{
								margin: [35, 0, 0, 0],
								table: {
									body: [
										[
											{
												text: "INVOICE NO.",
												style: ["boldHeader"],
											},
											{
												text: data.invNo,
												style: ["invoice_details"],
											},
										],
										[
											{
												text: "DATE",
												style: ["boldHeader"],
											},
											{ text: data.date, style: ["invoice_details"] },
										],
										[
											{
												text: "Credit Term",
												style: ["boldHeader"],
											},
											{
												text: data.creditTerm + " days",
												style: ["invoice_details"],
											},
										],
										[
											{
												text: "Due date",
												style: ["boldHeader"],
											},
											{ text: data.dueDate, style: ["invoice_details"] },
										],
										locationSection[0],
										locationSection[1],
									],
								},
								layout: {
									defaultBorder: false,
								},
							},
						],
					},
				],
			},
			{
				style: "table_main",
				table: {
					body: [
						[
							{
								text: "DESCRIPTION",
								border: [false, true, false, true],
								style: ["table_header", "boldHeader"],
							},
							{
								border: [false, true, false, true],
								text: "QUANTITY (kWh)",
								alignment: "right",
								style: ["table_header", "boldHeader"],
							},
							{
								border: [false, true, false, true],
								text: "UNIT COST",
								alignment: "right",
								style: ["table_header", "boldHeader"],
							},
							{
								border: [false, true, false, true],
								text: "TOTAL",
								alignment: "right",
								style: ["table_header", "boldHeader"],
							},
						],
						[
							{
								text: `Period: ${period}`,
								style: ["table_main_rows", "boldHeader"],

								colSpan: 4,
							},
						],
						[
							{
								text: `Electricity Production during ${peakLabels[0]} Time ${discountON_display}`,
								style: "table_main_rows",
							},
							{
								text: solarON,
								style: "table_main_rows",
								alignment: "right",
							},
							{
								text: priceON_disc,
								style: "table_main_rows",
								alignment: "right",
							},
							{
								text: formatNumber(totalPriceON, 2),
								style: ["table_main_rows", "boldHeader"],
								alignment: "right",
							},
						],
						[
							{
								text: `Electricity Production during ${peakLabels[1]} Time ${discountOFF_display}`,
								style: "table_main_rows",
							},
							{
								text: solarOFF,
								alignment: "right",
								style: "table_main_rows",
							},
							{
								text: priceOFF_disc,
								alignment: "right",
								style: "table_main_rows",
							},
							{
								text: formatNumber(totalPriceOFF, 2),
								alignment: "right",
								style: ["table_main_rows", "boldHeader"],
							},
						],
						!isNoFTCostSite
							? [
								{
									text: `Ft Cost ${discountFT_display}`,
									style: "table_main_rows",
								},
								{
									text: solarTotal,
									alignment: "right",
									style: "table_main_rows",
								},
								{
									text: FTcost_disc,
									alignment: "right",
									style: "table_main_rows",
								},
								{
									text: formatNumber(totalPriceFT, 2),
									alignment: "right",
									style: ["table_main_rows", "boldHeader"],
								},
							]
							: ["", "", "", ""],
						[
							{
								text: data.BPAFee > 0 ? "Basic Power Availability Fee" : "",
								style: "table_main_rows",
								rowSpan: 2,
							},
							{
								text: data.BPAFee > 0 ? "1.00" : "",
								alignment: "right",
								rowSpan: 2,
								style: "table_main_rows",
								padding: 0,
							},
							{
								text: data.BPAFee > 0 ? formatNumber(data.BPAFee, 2) : "",
								alignment: "right",
								rowSpan: 2,
								style: "table_main_rows",
							},
							{
								text: data.BPAFee > 0 ? formatNumber(data.BPAFee, 2) : "",
								alignment: "right",
								rowSpan: 2,
								style: ["table_main_rows", "boldHeader"],
							},
						],
						["EMPTY", "EMPTY", "EMPTY", "EMPTY"],
						[
							{
								colSpan: 3,
								text: "\nSubtotal",
								alignment: "right",
								style: ["table_main_rows", "boldHeader"],
							},
							"",
							"",
							{
								text: "\n" + formatNumber(subtotalPrice, 2),
								alignment: "right",
								style: ["table_main_rows", "boldHeader"],
							},
						],
						[
							{
								colSpan: 3,
								text: `VAT ${vatDisplay}%`,
								alignment: "right",
								style: ["table_main_rows", "boldHeader"],
							},
							"",
							"",
							{
								text: formatNumber(VATPrice, 2),
								alignment: "right",
								style: ["table_main_rows", "boldHeader"],
							},
						],
						[
							{
								colSpan: 3,
								text: specialDiscount_display,
								alignment: "right",
							},
							"",
							"",
							{
								text: discountPrice < 0 ? formatNumber(discountPrice, 2) : "",
								alignment: "right",
							},
						],
						[
							{
								colSpan: 3,
								text: "TOTAL DUE",
								alignment: "right",
								border: [false, true, false, true],
								style: "boldHeader",
								margin: [0, 1, 0, 0],
							},
							{ text: "", border: [false, true, false, true] },
							{ text: "", border: [false, true, false, true] },
							{
								text: formatNumber(totalDue, 2),
								alignment: "right",
								border: [false, true, false, true],
								style: "boldHeader",
								margin: [0, 1, 0, 0],
							},
						],
					],
					widths: ["55%", "15%", "15%", "15%"],
				},
				layout: {
					defaultBorder: false,
					paddingLeft: function (i, node) {
						return 0;
					},
					paddingRight: function (i, node) {
						return 0;
					},
					paddingTop: function (i, node) {
						return 0;
					},
					paddingBottom: function (i, node) {
						return 0;
					},
				},
			},
			{
				nodeName: "DIV",
				stack: [
					{
						text: "* This invoice is exempted from income tax under BOI promotion and is therefore not subject to withholding tax.",
						color: "red",
						italic: true,
					},
					{
						text: holidays != "" ? `*Holiday : ${holidays}` : "",
						color: "blue",
					},
					{
						text: data.specialComment != null ? data.specialComment : "",
					},
				],
				margin: [0, 4, 0, 8],
			},
			isNoFTCostSite
				? getNoFTCostElectricBillSection(param)
				: getElectricBillSection(param),
			
				// footer moved to page content for easy PDF merging in bulk download (1 click download)
				{
					nodeName: "DIV",
					absolutePosition: { x: 0, y: 700 },
					stack: [
						{
							stack: paymentSection,
							margin: [40, 0, 40, 20],
						},
						{
							text: [
								"This is a computer-generated Invoice. No signature is required"
							],
							style: {
								alignment: "center",
							},
						},
					]
				}
			]},
		],
		footer: [
			/* {
				columns: [
					{
						stack: [
							signature,
							{
								text: "..............................................................................",
								style: "sub_signature",
							},
							{
								text: `Authorized by ${authName}`,
								style: "sub_signature",
							},
						],
						style: "signature",
					},
					{
						stack: [
							{
								text: "..............................................................................",
								style: "sub_signature",
							},
							{
								text: "Received by: ___________________",
								style: "sub_signature",
							},
							{ text: "Date ....../....../......", style: "sub_signature" },
						],
						style: "signature",
					},
				],
				margin: [0, -105, 0, 0],
			}, */
			// {
			// 	stack: paymentSection,
			// 	margin: [40, -190, 0, 0],
			// },
			// {
			// 	stack: [
			// 		{
			// 			text: [
			// 				"This is a computer-generated Invoice. No signature is required"
			// 			],
			// 		},
			// 	],
			// 	margin: [40, 20, 40, 0],
			// 	style: {
			// 		alignment: "center",
			// 	},
			// },
		],
		styles: {
			header_lg: {
				fontSize: 27,
				margin: [0, 0, 0, 0],
			},
			sub_header: {
				decoration: "underline",
			},
			sub_header_2: {
				fontSize: 14,
				color: "#aaa",
				font: "Trirong",
				margin: [0, 0, 0, 0]
			},
			table_header: {
				margin: [0, 1, 0, 0],
				fontSize: 10,
			},
			table_main: {
				margin: [0, 5, 0, 0],
			},
			boldHeader: { bold: true },
			savings: {
				color: "green",
				bold: true,
			},
			signature: {
				alignment: "center",
			},
			sub_signature: {
				margin: [0, 5, 0, 0],
			},
			thai: {
				font: "Trirong",
				fontSize: 8,
				margin: [0, 0, 0, -3],
			},
			invoiceTHStyle: {
				font: "Calibri",
				fontSize: 9,
			}
		},
		defaultStyle: {
			font: "Calibri",
			fontSize: 9,
		},
	};
	return docDefinition;
}

function InvoiceTemplate_TH({ copyType='ORIGINAL', onDocDefinition=()=>{}, ...props }) {
	const user = props.user;
	const [formatNumber] = useFormatter();
	const solarCalc = useSolarCalc();
	const parseNo = solarCalc.parseFormatNumber;
	const [PDF, setPDF] = useState(null);
	const [PDFDoc, setPDFDoc] = useState(null);
	const [invoiceName, setInvoiceName] = useState(null);
	const logo = GYLogo;
	const signImg = accSignature;

	const selectedSite = props.selectedSite;
	const country = selectedSite.value.country;
	const peakLabels = usePeakLabels(country);

	const copyTypes = useMemo(() => ({
		ORIGINAL: "Original (ต้นฉบับ)",
		COPY: 'Copy (สำเนา)'
	}), []);

	// Show signature for client access
	const showSignature = user.roles.includes(
		"administration:InvoiceTable:_clientViewInv"
	);

	// Signature object
	const signObj = {
		image: signImg,
		height: 50,
		width: 107,
		absolutePosition: { x: -300, y: -145 },
	};

	// Signature section object
	const signature = showSignature ? signObj : {};

	/**
	 * Get discount remark in language requested
	 * @param {Language} lang
	 * @param {Discount} discount
	 * @param {MEA/PEA} EAtariff
	 */
	const getDiscountRemark = (lang, discount, EAtariff) => {
		switch (lang) {
			case "TH":
				return ` (ส่วนลดอัตราค่าไฟ ${parseFloat(discount)}% จาก ${EAtariff})`;

			default:
				return ` (with ${parseFloat(discount)}% discount from ${EAtariff}'s Tariff)`;
		}
	};

	const savingData = useSaving({ table_names: selectedSite?.table_name, month: selectedSite?.value?.selectedMonth });

	/**
	 * Get Tariff information in English and Thai language
	 * @param {Parameters of site} gyTariffParam
	 * @param {MEA/PEA} EAtariff
	 */
	const getGYTariff = (gyTariffParam, EAtariff, isNoFTCostSite) => {
		// Decimal place for BTG sites
		const dec = isNoFTCostSite ? 4 : 6;

		// On-Peak information
		const priceOn = gyTariffParam.priceON_disc.toFixed(dec);
		let ONpeakStr_eng = `${peakLabels[0]} (baht/kWh) = ${priceOn}`;
		let ONpeakStr_th = `ช่วงเวลา ${peakLabels[0]} (บาท/kWh) = ${priceOn}`;

		// Site has discount of On-Peak
		if (gyTariffParam.discountON > 0) {
			const discONPercent = formatNumber(gyTariffParam.discountON * 100, 2);
			ONpeakStr_eng += getDiscountRemark("ENG", discONPercent, EAtariff[0]);
			ONpeakStr_th += getDiscountRemark("TH", discONPercent, EAtariff[1]);
		}

		// Off-peak information
		const priceOff = gyTariffParam.priceOFF_disc.toFixed(dec);
		let OFFpeakStr_eng = `${peakLabels[1]} (baht/kWh) = ${priceOff}`;
		let OFFpeakStr_th = `ช่วงเวลา ${peakLabels[1]} (บาท/kWh) = ${priceOff}`;

		// Site has discount of Off-peak
		if (gyTariffParam.discountOFF > 0) {
			const discOFFPercent = formatNumber(gyTariffParam.discountOFF * 100, 2);
			OFFpeakStr_eng += getDiscountRemark("ENG", discOFFPercent, EAtariff[0]);
			OFFpeakStr_th += getDiscountRemark("TH", discOFFPercent, EAtariff[1]);
		}

		// FT cost information
		let FTStr_eng = "";
		let FTStr_th = "";

		// Site has discount of FT cost
		if (gyTariffParam.discountFT > 0) {
			const priceFT = gyTariffParam.FTcost_disc.toFixed(dec);
			FTStr_eng = `FT (baht/kWh) = ${priceFT}`;
			FTStr_th = `FT (บาท/kWh) = ${priceFT}`;

			const discFTPercent = formatNumber(gyTariffParam.discountFT * 100, 2);
			FTStr_eng += getDiscountRemark("ENG", discFTPercent, EAtariff[0]);
			FTStr_th += getDiscountRemark("TH", discFTPercent, EAtariff[1]);
		}

		return {
			ONpeakStr_eng,
			ONpeakStr_th,
			OFFpeakStr_eng,
			OFFpeakStr_th,
			FTStr_eng,
			FTStr_th,
		};
	};

	/**
	 * Set PDF name to be downloaded
	 * @param {Selected site} site
	 */
	const getInvoiceName = (site) => {
		let invName = site.invNo;
		let temp = site.customerID;

		switch (site.company) {
			case "CPRAM":
				temp = `_${temp.replace(" ", "_")}`;
				break;
			case "CP_ALL":
				temp = `_${temp.split(" ").reverse().join("")}`;
				break;
			case "Woodwork":
				temp = `_${temp.replace(" ", "")}`;
				break;
			case "Southeast Textile":
				temp = `_${temp.replace(" phase ", "")}`;
				break;
			case "SangRoong Group":
				temp = `_${temp}`;
				break;

			default:
				temp = "";
				break;
		}

		invName = `${site.invNo}${temp}`;
		return invName;
	};

	/**
	 * Get discount text
	 */
	const getDiscountText = (discount) =>
		`- Discount ${parseFloat(formatNumber(discount * 100, 2))}%`;

	/**
	 * Build location object
	 * @param {location string array} location
	 * @returns location object
	 */
	const buildLocationSection = (location) => {
		if (location != "")
				return [
					[
						{
							text: "Location:",
							style: ["invoice_details", { bold: true }],
						},
						{ text: location[0], style: ["invoice_details"] },
					],
					[
						{
							colSpan: 2,
							text: location[1] || '',
							style: ["invoice_details"],
						},
						"",
					],
				];
		else
			return [
				[
					{
						text: "",
					},
					{ text: "" },
				],
				[
					{
						colSpan: 2,
						text: "",
					},
					"",
				],
			];
	};

	/**
	 * Build entity section object
	 * @param {Solar entity object} solarEntity
	 * @returns Entity section object
	 */
	const buildEntitySection = (solarEntity, options={ isNewInv: false }) => {
		const addressArr = solarEntity.address.split("_");
		const addressRows = addressArr.map((rows) => ({ text: rows }));

		const section = [
			{
				text: solarEntity.name,
				style: ["boldHeader"],
			},
			...addressRows,
			{
				text: `TAX ID : ${solarEntity.taxID}`,
			},
			{
				text: `Contact person : ${!options.isNewInv ? solarEntity.accountantName : "Accounting"}`,
			},
			{
				text: `Tel: ${solarEntity.telephone}`,
			},
		];

		return section;
	};

	/**
	 * Build payment section object
	 * @param {Bank account obj} bankAccount
	 * @returns Payment section obj
	 */
	const buildPaymentSection = (bankAccount) => {
		const accNumber = bankAccount.accNumber;
		const accNumRemark = bankAccount.accNumRemark;

		let accountNumbers = accNumber != null ? accNumber.split(",") : ["-"];
		let accountRemarks = accNumRemark != null ? accNumRemark.split(",") : ["-"];

		const accName = bankAccount.accName != null ? bankAccount.accName : "-";
		const bankName = bankAccount.bankName != null ? bankAccount.bankName : "-";
		const branch = bankAccount.branch != null ? bankAccount.branch : "-";
		const swiftCode =
			bankAccount.swiftCode != null ? bankAccount.swiftCode : "-";

		let accNumSection = [];

		if (accountNumbers.length === accountRemarks.length) {
			accountNumbers = accountNumbers.map((str) => str.trim());
			accountRemarks = accountRemarks.map((str) => str.trim());

			accNumSection.push("Beneficiary Account number : ");

			for (let i = 0; i < accountNumbers.length; i++) {
				const accNumberStr = { text: accountNumbers[i], style: "boldHeader" };
				const accRemarkStr = { text: ` [${accountRemarks[i]}]  ` };

				accNumSection.push(accNumberStr);
				accNumSection.push(accRemarkStr);
			}
		}

		const section = [
			{
				text: [
					"Please make the payment to ",
					{
						text: accName,
						style: "boldHeader",
					},
				],
			},
			{
				text: [
					"Bank : ",
					{ text: `${bankName}, `, style: "boldHeader" },
					"Branch : ",
					{ text: branch, style: "boldHeader" },
				],
			},
			{
				text: accNumSection,
			},
			{
				text: ["Bank Swift Code : ", { text: swiftCode, style: "boldHeader" }],
			},
		];

		return section;
	};

	/**
	 * Payment section for S2 Solar Entity
	 * @param {*} bankAccount 
	 * @returns PDF make element Object
	 */
	const buildPaymentSectionExIm = (bankAccount, solarEntity) => {
		const accNumber = bankAccount.accNumber;
		const accNumRemark = bankAccount.accNumRemark;

		let accountNumbers = accNumber != null ? accNumber.split(",") : ["-"];
		let accountRemarks = accNumRemark != null ? accNumRemark.split(",") : ["-"];

		const accName = bankAccount.accName != null ? bankAccount.accName : "-";
		const bankName = bankAccount.bankName != null ? bankAccount.bankName : "-";
		const branch = bankAccount.branch != null ? bankAccount.branch : "-";
		const swiftCode =
			bankAccount.swiftCode != null ? bankAccount.swiftCode : "-";

		const refNumbers = {
			S2: ["000001201049423015", "100401500135313"],
			S3: ["000001201301423020", "100402000153604"]
		}

		const section = [
			{
				text: [
					"Please make the payment to ",
					{
						text: accName,
						style: "boldHeader",
					},
				],
			},
			{
				nodeName: "DIV",
				columns: [
					{
						nodeName: "DIV",
						width: "60%",
						margin: [0, 2, 0, 0],
						lineHeight: 1.1,
						stack: [
							{
								text: [
									{ text: "By cheque: ", style: "boldHeader", decoration: "underline" },
									{ text: "*** Deposit to Krungthai Bank and Kasikorn Bank only ***", style: "boldHeader" }
								],
								margin: [0, 0, 0, 2]
							},
							{
								text: "Payee Name: Export-Import Bank of Thailand"
							},
							{
								text: `Reference 1: ${refNumbers[solarEntity][0]}`
							},
							{
								text: `Reference 2: ${refNumbers[solarEntity][1]}`
							}
						]
					},
					{
						nodeName: "DIV",
						style: 'thai',
						lineHeight: 0.7,
						width: "40%",
						stack: [
							{
								text: "By Transfer or Bill Payment:",
								style: 'boldHeader',
								decoration: "underline",
								lineHeight: 1,
							},
							{
								text: "Name: ธสน. เพื่อชำระสินเชื่อ"
							},
							{
								text: `Reference 1: ${refNumbers[solarEntity][0]}`
							},
							{
								text: `Reference 2: ${refNumbers[solarEntity][1]}`
							}
						]
					}
				]
			}
		];

		return section;
	};

	/**
	 * Create payment section
	 * starting from 2023-10-31, S2 solar entity will have different payment section
	 * @param {*} solarEntity 
	 * @param {*} bankAccount 
	 * @returns PDFmake element object
	 */
	const getPaymentSection = (siteCode, solarEntity, bankAccount) => {
		if(solarEntity.entityID === 'S3' && moment(selectedSite.value.date, 'DD/MM/YYYY').endOf('month').startOf('day').diff(moment('2024-01-31', 'YYYY-MM-DD')) >= 0 && siteCode !== "UTH01" && siteCode !== "UTH02") { // show normal bank account for 2 Unilever sites in TH (Minburi / Gateway)
			return buildPaymentSectionExIm(bankAccount, solarEntity.entityID);
		}
		if((solarEntity.entityID === "S2") && moment(selectedSite.value.date, 'DD/MM/YYYY').endOf('month').startOf('day').diff(moment('2023-10-31', 'YYYY-MM-DD')) >= 0) {
			return buildPaymentSectionExIm(bankAccount, solarEntity.entityID);
		}
		return buildPaymentSection(bankAccount);
	}


	/**
	 * Generate invoice
	 * @param {Selected Site} selectedSite
	 */
	const generateInvoice = async (selectedSite) => {
		const data = selectedSite.value;
		const holidays =
			selectedSite.holidays != "" && selectedSite.holidays != null
				? selectedSite.holidays
				: "";

		const selectedDate = moment(data.date, "DD/MM/YYYY", true)
		const endMonth = moment(selectedDate).endOf('month')
		const startMonth = moment(selectedDate).startOf('month')
		const _cod = moment(data.cod)
		const diff = startMonth.diff(_cod, "years", true)
		const period = (diff > 0 ? startMonth.format("DD/MM/YYYY") : _cod.format("DD/MM/YYYY")) + " - " + endMonth.format("DD/MM/YYYY")

		const location = data.location.split("_");
		const billingName = data.billingName != null ? data.billingName : "-";
		const billingAddress =
			data.billingAddress != null ? data.billingAddress : "-";

		const isNoFTCostSite = data.FTcost == null;
		const solarEntity = data.solarEntity;
		const bankAccount = data.bankAccount;

		const tempEntityName = solarEntity.name;
		const authName = tempEntityName.replace(" (Head Office)", "");

		const tariffData = data.tariffData;
		const tariffID = tariffData.tariffID.split("/")[0];
		const tariffType = tariffID.split("_");
		const tariffDesc = tariffData.tariffDesc;
		const EAEng = tariffData.EAEng.split("_");
		const EAThai = tariffData.EAThai.split("_");

		// Set PDF document name
		setInvoiceName(getInvoiceName(data));

		// Grid energy reinjection status
		const isGridReinjectApplied = data.isGridReinjectApplied;

		// Solar Production values (with grid energy deduction when applicable)
		let solarON = data.solarON;
		let solarOFF = data.solarOFF;
		let solarTotal = data.solarTotal;
		if (isGridReinjectApplied) {
			solarON = data.solarON - (data.deliveredOn || 0);
			solarOFF = data.solarOFF - (data.deliveredOff || 0);
			solarTotal = data.solarTotal - ((data.deliveredOn || 0) + (data.deliveredOff || 0));
		}
		const solarON_display = formatNumber(solarON, 3);
		const solarOFF_display = formatNumber(solarOFF, 3);
		const solarTotal_display = formatNumber(solarTotal, 3);

		// Discount text
		const discountON_display =
			data.discountON > 0 ? getDiscountText(data.discountON) : "";
		const discountOFF_display =
			data.discountOFF > 0 ? getDiscountText(data.discountOFF) : "";
		const discountFT_display =
			data.discountFT > 0 ? getDiscountText(data.discountFT) : "";

		// Special decimal place for unit price calculations
		const specialDP = isNoFTCostSite ? 4 : null;

		// Discounted Price
		const priceON_disc = solarCalc.calculateUnitCost(
			data.priceON,
			data.discountON,
			data.currency,
			specialDP
		);
		const priceOFF_disc = solarCalc.calculateUnitCost(
			data.priceOFF,
			data.discountOFF,
			data.currency,
			specialDP
		);
		const FTcost_disc = solarCalc.calculateUnitCost(
			data.FTcost,
			data.discountFT,
			data.currency,
			specialDP
		);

		// Tariff section
		const gyTariffParam = {
			discountON: data.discountON,
			discountOFF: data.discountOFF,
			discountFT: data.discountFT,
			priceON_disc,
			priceOFF_disc,
			FTcost_disc,
		};

		const GYTariff_remarks = getGYTariff(
			gyTariffParam,
			[EAEng[1], EAThai[1]],
			isNoFTCostSite
		);

		// Savings and Cost calculations
		const prod = {
			ON: solarON,
			OFF: solarOFF,
			TOTAL: solarTotal,
		};
		const price = {
			ON: data.priceON,
			OFF: data.priceOFF,
			FT: data.FTcost,
		};
		const discount = {
			ON: data.discountON,
			OFF: data.discountOFF,
			FT: data.discountFT,
		};

		// Savings and cost calculations results
		// const savingsObj = solarCalc.calculateSavings(
		// 	prod,
		// 	price,
		// 	discount,
		// 	data.currency
		// );
		const costsObj = solarCalc.calculateProdCost(
			prod,
			price,
			discount,
			data.currency,
			specialDP
		);

		// Savings
		// const savings = savingsObj.savingsTotal;
		const savings = savingData[savingData.length - 1]?.saving;

		// Prices
		const totalPriceON = costsObj.costON;
		const totalPriceOFF = costsObj.costOFF;
		const totalPriceFT = costsObj.costFT;

		// Subtotal price
		let subtotalPrice = costsObj.costTotal;

		// Only for sites with BPA fee
		if (data.BPAFee > 0) subtotalPrice = subtotalPrice + data.BPAFee;

		// VAT calculations
		const currency = data.currency;
		const vatPercent = data.vat;
		const vatDisplay = parseNo(data.vat * 100);

		const VATObj = solarCalc.calculateVAT(subtotalPrice, currency, vatPercent);
		const VATPrice = VATObj.VAT;
		const tempTotal = VATObj.totalDue;

		let discountPrice = 0;
		let totalDue = 0;
		let specialDiscount_display = "";

		// Special discounts
		if (data.specialDiscount === null) totalDue = tempTotal;
		else {
			const specialDiscount = parseFloat(formatNumber(data.specialDiscount * 100, 2));
			specialDiscount_display = `Discount ${specialDiscount}%`;
			discountPrice = -(tempTotal * data.specialDiscount);

			totalDue =
				parseFloat(subtotalPrice.toFixed(2)) +
				parseFloat(VATPrice.toFixed(2)) +
				parseFloat(discountPrice.toFixed(2));
		}

		// Payment section
		const paymentSection = getPaymentSection(data.siteCode, solarEntity, bankAccount);

		// Electricity bill section parameters
		const discountON = parseFloat(formatNumber(discount.ON * 100, 2));
		const discRemEN = getDiscountRemark("EN", discountON, EAEng[1]);
		const discRemTH = getDiscountRemark("TH", discountON, EAThai[1]);

		const param = {
			tariffType,
			tariffDesc,
			peakLabels,
			EAEng,
			EAThai,
			discRemEN,
			discRemTH,
			priceON: priceON_disc,
			data,
			GYTariff_remarks,
			savings,
		};

		let docDefinition;

		const locationSection = buildLocationSection(location);

		// invoice template TH is changed to match a new design of envelope since 08/31/23
		if(selectedDate.diff(moment("2023-08-31", "YYYY-MM-DD")) < 0) {
			// Location section
			const entitySection = buildEntitySection(solarEntity, { isNewInv: false });
			// doc definition data
			const docDefData = { logo, entitySection, billingName, billingAddress, data, copyTypes, copyType, locationSection, period, peakLabels, discountON_display, solarON, priceON_disc, formatNumber, totalPriceON, discountOFF_display, solarOFF, priceOFF_disc, totalPriceOFF, isNoFTCostSite, discountFT_display, solarTotal, FTcost_disc, totalPriceFT, subtotalPrice, vatDisplay, VATPrice, specialDiscount_display, discountPrice, totalDue, holidays, param, paymentSection, isGridReinjectApplied };
			// generate doc definition for old invoice
			docDefinition = old_invoice_template(docDefData);
		} else {
			// below is for new invoice TH template for new envelope design since 08/31/23
			const custm_envelope = await GYAxios.get(`/invoice/billing_address/${data.siteID}`).then(res => res.data);
			const mail_bill_add = custm_envelope?.mail_bill_add; // site name and address combined
			const mail_name = custm_envelope?.mail_name; // name of person (from client side) who receives this invoice
			const mail_phone_no = custm_envelope?.mail_phone_no;

			// Location section
			const entitySection = buildEntitySection(solarEntity, { isNewInv: true });

			// PDF config
			docDefinition = {
				pageSize: "A4",
				pageMargins: 0,
				content: [
					{ nodeName: 'DIV', style: 'invoiceTHStyle', stack: [
					{
						nodeName: "DIV",
						margin: [ cm_to_pt(2), cm_to_pt(1.9), 0, 0 ],
						columns: [
							{
								nodeName: "DIV",
								width: cm_to_pt(9.5),
								stack: [
									{
										image: logo,
										width: 150,
									},
									...entitySection
								],
							},
							{
								margin: [ cm_to_pt(0.5), 0, 0, 0 ],
								table: {
									headerRows: 1,
									widths: [cm_to_pt(7.5)],
									heights: [cm_to_pt(3.8)],
									body: [[
										{
											nodeName: "DIV",
											stack: [
												{ text: "Invoice / Tax Invoice", style: "header_lg_new" },
												{ text: copyTypes[copyType], style: "sub_header_2_new", margin: 0 },
												{
													nodeName: "DIV",
													style: "address_window_text",
													stack: [
														{ text: mail_bill_add ? `Send to:\n${mail_bill_add}` : `Send to: ${billingName}\n${billingAddress}`, lineHeight: 0.8, style: ['address_window_send_to'] },
														{ text: mail_name ? `Attn: ${mail_name}` : "", lineHeight: 0.8 },
														{ text: mail_phone_no ? `Tel: ${mail_phone_no}` : "", lineHeight: 0.8 },
													]
												}
											]
										}
									]],
								},
								layout: 'noBorders',
							},
						],
					},
					{
						nodeName: 'DIV',
						margin: [ cm_to_pt(2), 0 ],
						columns: [
							{
								nodeName: "DIV",
								margin: [20, 10],
								width: '50%',
								stack: [
									{
										text: billingName,
										style: ["boldHeader"],
									},
									{
										text: billingAddress,
									},
									{
										text: `TAX ID : ${data.taxID}`,
									},
								],
							},
							{
								margin: [20, 10],
								width: '50%',
								table: {
									widths: [cm_to_pt(1.8), '*'],
									body: [
										[
											{
												text: "INVOICE NO.",
												style: ["boldHeader"],
											},
											{
												text: data.invNo,
												style: ["invoice_details"],
											},
										],
										[
											{
												text: "DATE",
												style: ["boldHeader"],
											},
											{ text: data.date, style: ["invoice_details"] },
										],
										[
											{
												text: "Credit Term",
												style: ["boldHeader"],
											},
											{
												text: data.creditTerm + " days",
												style: ["invoice_details"],
											},
										],
										[
											{
												text: "Due date",
												style: ["boldHeader"],
											},
											{ text: data.dueDate, style: ["invoice_details"] },
										],
										locationSection[0],
										locationSection[1],
									],
								},
								layout: {
									defaultBorder: false,
								},
							}
						]
					},
					{
						style: "table_main",
						margin: [ cm_to_pt(2), 0 ],
						table: {
							body: [
								[
									{
										text: "DESCRIPTION",
										border: [false, true, false, true],
										style: ["table_header", "boldHeader"],
									},
									{
										border: [false, true, false, true],
										text: "QUANTITY (kWh)",
										alignment: "right",
										style: ["table_header", "boldHeader"],
									},
									{
										border: [false, true, false, true],
										text: "UNIT COST",
										alignment: "right",
										style: ["table_header", "boldHeader"],
									},
									{
										border: [false, true, false, true],
										text: "TOTAL",
										alignment: "right",
										style: ["table_header", "boldHeader"],
									},
								],
								[
									{
										text: `Period: ${period}`,
										style: ["table_main_rows", "boldHeader"],

										colSpan: 4,
									},
								],
								[
									{
										text: `Electricity Production during ${peakLabels[0]} Time ${discountON_display}`,
										style: "table_main_rows",
									},
									{
										text: solarON_display,
										style: "table_main_rows",
										alignment: "right",
									},
									{
										text: priceON_disc,
										style: "table_main_rows",
										alignment: "right",
									},
									{
										text: formatNumber(totalPriceON, 2),
										style: ["table_main_rows", "boldHeader"],
										alignment: "right",
									},
								],
								[
									{
										text: `Electricity Production during ${peakLabels[1]} Time ${discountOFF_display}`,
										style: "table_main_rows",
									},
									{
										text: solarOFF_display,
										alignment: "right",
										style: "table_main_rows",
									},
									{
										text: priceOFF_disc,
										alignment: "right",
										style: "table_main_rows",
									},
									{
										text: formatNumber(totalPriceOFF, 2),
										alignment: "right",
										style: ["table_main_rows", "boldHeader"],
									},
								],
								!isNoFTCostSite
									? [
										{
											text: `Ft Cost ${discountFT_display}`,
											style: "table_main_rows",
										},
										{
											text: solarTotal_display,
											alignment: "right",
											style: "table_main_rows",
										},
										{
											text: FTcost_disc,
											alignment: "right",
											style: "table_main_rows",
										},
										{
											text: formatNumber(totalPriceFT, 2),
											alignment: "right",
											style: ["table_main_rows", "boldHeader"],
										},
									]
									: ["", "", "", ""],
								...(data.BPAFee > 0 ? [[
									{
										text:"Basic Power Availability Fee",
										style: "table_main_rows",
										rowSpan: 2,
									},
									{
										text:"1.00",
										alignment: "right",
										rowSpan: 2,
										style: "table_main_rows",
										padding: 0,
									},
									{
										text:formatNumber(data.BPAFee, 2),
										alignment: "right",
										rowSpan: 2,
										style: "table_main_rows",
									},
									{
										text:formatNumber(data.BPAFee, 2),
										alignment: "right",
										rowSpan: 2,
										style: ["table_main_rows", "boldHeader"],
									},
								]] : []),
								[" ", "", "", ""], // empty row
								[
									{
										colSpan: 3,
										text: "\nSubtotal",
										alignment: "right",
										style: ["table_main_rows", "boldHeader"],
									},
									"",
									"",
									{
										text: "\n" + formatNumber(subtotalPrice, 2),
										alignment: "right",
										style: ["table_main_rows", "boldHeader"],
									},
								],
								[
									{
										colSpan: 3,
										text: `VAT ${vatDisplay}%`,
										alignment: "right",
										style: ["table_main_rows", "boldHeader"],
									},
									"",
									"",
									{
										text: formatNumber(VATPrice, 2),
										alignment: "right",
										style: ["table_main_rows", "boldHeader"],
									},
								],
								[
									{
										colSpan: 3,
										text: specialDiscount_display,
										alignment: "right",
									},
									"",
									"",
									{
										text: discountPrice < 0 ? formatNumber(discountPrice, 2) : "",
										alignment: "right",
									},
								],
								[
									{
										colSpan: 3,
										text: "TOTAL DUE",
										alignment: "right",
										border: [false, true, false, true],
										style: "boldHeader",
										margin: [0, 1, 0, 0],
									},
									{ text: "", border: [false, true, false, true] },
									{ text: "", border: [false, true, false, true] },
									{
										text: formatNumber(totalDue, 2),
										alignment: "right",
										border: [false, true, false, true],
										style: "boldHeader",
										margin: [0, 1, 0, 0],
									},
								],
							],
							widths: ["55%", "15%", "15%", "15%"],
							// widths: [ cm_to_pt(3), '*', '*', '*' ],
							heights: 9,
						},
						layout: {
							defaultBorder: false,
							paddingLeft: function (i, node) {
								return 0;
							},
							paddingRight: function (i, node) {
								return 0;
							},
							paddingTop: function (i, node) {
								return 0;
							},
							paddingBottom: function (i, node) {
								return 0;
							},
						},
					},
					{
						nodeName: "DIV",
						stack: [
							{
								text: "* This invoice is exempted from income tax under BOI promotion and is therefore not subject to withholding tax.",
								color: "red",
								italic: true,
							},
							{
								text: holidays != "" ? `*Holiday : ${holidays}` : "",
								color: "blue",
							},
							{
								text: data.specialComment != null ? data.specialComment : "",
							},
						],
						margin: [cm_to_pt(2), 4, cm_to_pt(2), 8],
					},
					{
						nodeName: "DIV",
						margin: [ cm_to_pt(2), 0 ],
						stack: [isNoFTCostSite ? getNoFTCostElectricBillSection(param) : getElectricBillSection(param)]
					},
					
						// footer moved to page content for easy PDF merging in bulk download (1 click download)
						{
							nodeName: "DIV",
							absolutePosition: { x: 0, y: cm_to_pt(26) },
							stack: [
								{
									stack: paymentSection,
									margin: [cm_to_pt(2), 0, cm_to_pt(2), cm_to_pt(0.5)],
								},
								{
									text: [
										"This is a computer-generated Invoice. No signature is required"
									],
									style: {
										alignment: "center",
									},
								},
							]
						}
					]},
				],
				footer: [
					/* {
						columns: [
							{
								stack: [
									signature,
									{
										text: "..............................................................................",
										style: "sub_signature",
									},
									{
										text: `Authorized by ${authName}`,
										style: "sub_signature",
									},
								],
								style: "signature",
							},
							{
								stack: [
									{
										text: "..............................................................................",
										style: "sub_signature",
									},
									{
										text: "Received by: ___________________",
										style: "sub_signature",
									},
									{ text: "Date ....../....../......", style: "sub_signature" },
								],
								style: "signature",
							},
						],
						margin: [0, -105, 0, 0],
					}, */
					// {
					// 	stack: paymentSection,
					// 	margin: [40, -190, 0, 0],
					// },
					// {
					// 	stack: [
					// 		{
					// 			text: [
					// 				"This is a computer-generated Invoice. No signature is required"
					// 			],
					// 		},
					// 	],
					// 	margin: [40, 20, 40, 0],
					// 	style: {
					// 		alignment: "center",
					// 	},
					// },
				],
				styles: {
					header_lg_new: {
						fontSize: 13,
						margin: [0, 0, 0, 0],
						alignment: 'right',
					},
					sub_header: {
						decoration: "underline",
					},
					sub_header_2_new: {
						fontSize: 10,
						color: "#aaa",
						font: "Trirong",
						margin: [0, -3, 0, 0],
						alignment: 'right',
					},
					address_window_text: {
						fontSize: 8,
						font: "Trirong",
						margin: 0
					},
					address_window_send_to: {
						bold: true,
					},
					table_header: {
						margin: [0, 1, 0, 0],
						fontSize: 10,
					},
					table_main: {
						margin: [0, 5, 0, 0],
					},
					table_main_rows: {
						margin: 0,
					},
					boldHeader: { bold: true },
					savings: {
						color: "green",
						bold: true,
					},
					signature: {
						alignment: "center",
					},
					sub_signature: {
						margin: [0, 5, 0, 0],
					},
					thai: {
						font: "Trirong",
						fontSize: 8,
						margin: [0, 0, 0, -3],
					},
					invoiceTHStyle: {
						font: "Calibri",
						fontSize: 9,
					}					
				},
				defaultStyle: {
					font: "Calibri",
					fontSize: 9,
				},
			};
		}

		onDocDefinition(clone(docDefinition));

		// Generate PDFMAKE document
		const pdfDocGenerator = pdfMake.createPdf(docDefinition);

		// Set PDFMAKE config for download in the later stage
		setPDFDoc(docDefinition);

		if (PDF == null && selectedSite != null) {
			// Get dataURL of document for download
			pdfDocGenerator.getDataUrl((dataUrl) => {
				setPDF(dataUrl);
			});
		}
	};

	/**
	 * Download PDF document
	 * @returns PDF document
	 */
	const downloadPDF = () =>
		pdfMake.createPdf(PDFDoc).download(`${invoiceName}.pdf`);

	useMemo(() => {
		const labelsAreReady = !peakLabels.includes("");

		if (props.selectedSite != null && labelsAreReady && savingData.length > 0)
			generateInvoice(props.selectedSite);

		return () => setInvoiceName(null);
	}, [props.selectedSite, peakLabels, savingData]);

	return (
		<>
			{PDF != null ? (
				<>
					<iframe style={{ width: "98%", height: "72vh" }} src={PDF} />
					<Row>
						<Button onClick={downloadPDF}>Download</Button>
					</Row>
				</>
			) : (
				<LoaderGraph style={{ height: "72vh" }} />
			)}
		</>
	);
}

export default InvoiceTemplate_TH;
